import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './KeyStats.module.css';

function fmtNum(n, decimals = 2) {
  if (!n && n !== 0) return '—';
  return n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function fmtVol(n) {
  if (!n) return '—';
  if (n >= 1e9) return (n / 1e9).toFixed(2) + 'B';
  if (n >= 1e6) return (n / 1e6).toFixed(2) + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(1) + 'K';
  return n.toString();
}

function fmtCap(n) {
  if (!n) return '—';
  if (n >= 1e12) return '$' + (n / 1e12).toFixed(2) + 'T';
  if (n >= 1e9)  return '$' + (n / 1e9).toFixed(2) + 'B';
  if (n >= 1e6)  return '$' + (n / 1e6).toFixed(2) + 'M';
  return '$' + n.toLocaleString();
}

function fmtPct(n) {
  if (!n && n !== 0) return '—';
  const pct = Math.abs(n) < 2 ? n * 100 : n;
  return pct.toFixed(2) + '%';
}

function Stat({ label, value }) {
  return (
    <div className={styles.stat}>
      <div className={styles.statLabel}>{label}</div>
      <div className={styles.statValue}>{value ?? '—'}</div>
    </div>
  );
}

export default function KeyStats({ financials }) {
  const { t } = useTranslation();
  const f = financials;

  const rows = [
    { label: t('key_stats.bid'),            value: f.bid    ? `${fmtNum(f.bid)} × ${f.bidSize  || 0}` : '—' },
    { label: t('key_stats.ask'),            value: f.ask    ? `${fmtNum(f.ask)} × ${f.askSize  || 0}` : '—' },
    { label: t('key_stats.day_range'),      value: (f.dayLow || f.dayHigh) ? `${fmtNum(f.dayLow)} – ${fmtNum(f.dayHigh)}` : '—' },
    { label: t('key_stats.week_52_range'),  value: (f.fiftyTwoWeekLow || f.fiftyTwoWeekHigh) ? `${fmtNum(f.fiftyTwoWeekLow)} – ${fmtNum(f.fiftyTwoWeekHigh)}` : '—' },
    { label: t('key_stats.volume'),         value: fmtVol(f.volume) },
    { label: t('key_stats.avg_volume'),     value: fmtVol(f.avgVolume) },
    { label: t('key_stats.market_cap'),     value: fmtCap(f.marketCap) },
    { label: t('key_stats.beta'),           value: f.beta    ? fmtNum(f.beta)  : '—' },
    { label: t('key_stats.pe_trailing'),    value: f.trailingPE ? fmtNum(f.trailingPE) : '—' },
    { label: t('key_stats.pe_forward'),     value: f.forwardPE  ? fmtNum(f.forwardPE)  : '—' },
    { label: t('key_stats.eps_trailing'),   value: f.trailingEps ? fmtNum(f.trailingEps) : '—' },
    { label: t('key_stats.eps_forward'),    value: f.forwardEps  ? fmtNum(f.forwardEps)  : '—' },
    { label: t('key_stats.price_book'),     value: f.priceToBook ? fmtNum(f.priceToBook) : '—' },
    { label: t('key_stats.book_value'),     value: f.bookValue   ? fmtNum(f.bookValue)   : '—' },
    { label: t('key_stats.dividend_rate'),  value: f.dividendRate  ? `$${fmtNum(f.dividendRate)}` : '—' },
    { label: t('key_stats.dividend_yield'), value: f.dividendYield ? fmtPct(f.dividendYield) : '—' },
  ];

  return (
    <div className={styles.wrap}>
      <h3 className={styles.title}>{t('key_stats.title')}</h3>
      <div className={styles.grid}>
        {rows.map(r => <Stat key={r.label} label={r.label} value={r.value} />)}
      </div>
    </div>
  );
}
